'use client'

import { Category } from '@/payload-types'
import type { Media } from '@/payload-types'
import CategoryCard from './CategoryCard'
import React from 'react'
import classes from './index.module.css'
import cardClasses from './CategoryCard.module.css'
import { Link } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import { SectionHeader } from '@/components/ui/SectionHeader'

import { cn } from '@/utilities/cn'

type ShopByCategoriesProps = {
  categories: (Category & { fallbackImage?: Media | null })[]
  /** How many more categories exist beyond the displayed ones (CAT-3).
   *  When > 0 a "+N mehr" tile is appended to the grid linking to /categories. */
  remaining?: number
}

const ShopByCategories = ({ categories, remaining = 0 }: ShopByCategoriesProps) => {
  const t = useTranslations('product')
  const commonT = useTranslations('common')

  if (!categories || categories.length === 0) {
    return <div className={classes.noCategories}>{t('noCategories')}</div>
  }

  // CAT-4 fix: bento slot positions are index-mapped (0=large, 7=wide).
  // `sort: 'title'` in page.tsx is alphabetical, so any category that happens
  // to be first alphabetically gets the hero slot. Curated slug order pins
  // specific categories to specific bento slots. Slugs not in the list fall
  // back to alphabetical order (preserves Wave 3 behaviour for future adds).
  const BENTO_SLUG_ORDER = [
    'angebote',
    'headshop',
    'flohmarkt',
    'cbd',
    'ethnobotanik',
    'growshop',
    'hanfpflanzen',
    'hanfsamen',
  ] as const

  const orderedCategories = [...categories].sort((a, b) => {
    const ai = BENTO_SLUG_ORDER.indexOf(a.slug as (typeof BENTO_SLUG_ORDER)[number])
    const bi = BENTO_SLUG_ORDER.indexOf(b.slug as (typeof BENTO_SLUG_ORDER)[number])
    if (ai === -1 && bi === -1) return a.title.localeCompare(b.title)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

  return (
    <div className="w-full max-w-[80rem] mx-auto my-3">
      {/* Experimental peel-back (matches FeaturedProducts): no glass bar, no glass
          container — the heading and category tiles sit straight on the lava. */}
      <SectionHeader
        title={t('categories')}
        href="/categories"
        buttonLabel={commonT('viewAll')}
        testId="categories-title"
        bare
      />
      <section
        data-testid="shop-by-categories"
        className="w-full px-1 sm:px-2 lg:px-3 py-2 sm:py-3 lg:py-4"
      >
        <div
          data-testid="categories-grid"
          className={cn(classes.bentoGrid, 'px-4 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-6')}
        >
          <span className={`${classes.gridDot} hidden lg:block`} style={{ top: 'calc(200px + 6px)', left: '50%' }} />
          <span className={`${classes.gridDot} hidden lg:block`} style={{ top: 'calc(400px + 18px)', left: '50%' }} />
          <span className={`${classes.gridDot} hidden lg:block`} style={{ top: 'calc(400px + 18px)', left: '25%' }} />
          <span className={`${classes.gridDot} hidden lg:block`} style={{ top: 'calc(400px + 18px)', right: '25%' }} />

          {orderedCategories.map((category, index) => (
            <CategoryCard key={category.id} category={category} index={index} fallbackImage={category.fallbackImage} />
          ))}

          {remaining > 0 && (
            <div className={`${cardClasses.cardWrapper} ${classes.spanSmall}`}>
              <Link
                href="/categories"
                className={`${cardClasses.card} ${cardClasses.hoverDiagBR}`}
                data-testid="category-card-more"
                aria-label={t('moreCategories', { count: remaining })}
              >
                <div className={cardClasses.moreContent}>
                  <span className={cardClasses.moreCount}>+{remaining}</span>
                  <span className={cardClasses.moreLabel}>{t('moreCategoriesLabel')}</span>
                  <span className={cardClasses.moreArrow} aria-hidden="true">→</span>
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default ShopByCategories

